import { useAccountUsage, UseAccountUsageResponse } from 'api/BillingApi';
import { useDateContext } from 'modules/billing/contexts/DateContext';
import { DimensionSet, UsageMetricGroups } from 'modules/domains/billing';
import { addNegativeToast } from 'modules/toastContainer';
import { FC, PropsWithChildren } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ProjectQueryContext } from './ProjectQueryContext';
import { useQueryContext } from './QueryContext';
import { selectFeatureFlags } from 'core/userContext';

const projectAggLevels = [DimensionSet.ByOrgProject, DimensionSet.ByOrgProjectJourney].join(',');

export const ProjectQueryContextProvider: FC<PropsWithChildren> = ({ children }) => {
  const dispatch = useDispatch();
  const { billingPeriod } = useDateContext();
  const { isFetched } = useQueryContext();
  const { BillingWhatsAppReporting, WhatsAppMessageChannelGA } = useSelector(selectFeatureFlags);
  const isWhatsAppEnabled = BillingWhatsAppReporting && WhatsAppMessageChannelGA;

  const projectMetricGroups = Object.values(UsageMetricGroups)
    .filter((group) => group !== UsageMetricGroups.WhatsApp || isWhatsAppEnabled)
    .join(',');

  const {
    data: { projects = {}, values = {} } = {} as UseAccountUsageResponse,
    isError,
    isLoading,
  } = useAccountUsage(
    {
      aggLevels: projectAggLevels,
      endDatePST: billingPeriod.endDatePST,
      metricGroups: projectMetricGroups,
      startDatePST: billingPeriod.startDatePST,
    },
    {
      enabled: isFetched && !!billingPeriod.endDatePST && !!billingPeriod.startDatePST,
      retry: false,
      refetchOnWindowFocus: false,
      onError: () => {
        dispatch(
          addNegativeToast(
            'Trouble fetching project usage data. Please try again, or contact your account manager.',
          ),
        );
      },
    },
  );

  const value = {
    isError,
    isLoading,
    projects,
    projectUsageMetrics: values,
  };

  return <ProjectQueryContext.Provider value={value}>{children}</ProjectQueryContext.Provider>;
};
